import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tooltip } from '@material-ui/core';
import { EditTextField } from '@components/Pages/LettersPage/Input';
import { translate } from '@utils';
import { Indicator, RenameIcon } from '@images/icons';
import {
  currentDescriptionDisplayedSelector,
  currentSubmissionDescriptionSelector,
  currentSubmissionSelector,
  currentSubmissionTitleSelector,
  submissionHasChangesSelector,
  titleInputDisabledSelector,
} from '@selectors/submission';
import debounce from 'lodash/debounce';
import { setCurrentSubmission } from '@actions/submissionActions';
import cx from 'classnames';

export const EditTextFieldContainer: FC = () => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const descriptionRef = useRef(null);

  const currentSubmissionTitle = useSelector(currentSubmissionTitleSelector);
  const currentSubmissionDescription = useSelector(currentSubmissionDescriptionSelector);
  const currentSubmission = useSelector(currentSubmissionSelector);
  const isInputDisabled = useSelector(titleInputDisabledSelector);
  const isSubmissionHasChanges = useSelector(submissionHasChangesSelector);
  const isCurrentDescriptionDisplayed = useSelector(currentDescriptionDisplayedSelector);

  const [isInputEnabled, setIsInputEnabled] = useState(false);
  const [isDescriptionInputEnabled, setIsDescriptionInputEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    setTitle(currentSubmissionTitle);
  }, [currentSubmissionTitle]);

  useEffect(() => {
    if (currentSubmissionDescription) {
      setDescription(currentSubmissionDescription);
      setIsDescriptionInputEnabled(false);
    } else {
      setDescription('');
      setIsDescriptionInputEnabled(true);
    }
  }, [currentSubmissionDescription]);

  const handleChangeEdit = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTitle(event.target.value.trimStart());
    },
    [title]
  );

  const handleDescriptionChangeEdit = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDescription(event.target.value.trimStart());
    },
    [description]
  );

  const handleConfirmationEdit = useCallback(() => {
    if (title && title !== currentSubmissionTitle) {
      const value = title.slice(0, 30);
      setTitle(value);
      dispatch(setCurrentSubmission({ ...currentSubmission, title: value }));
    } else {
      setTitle(currentSubmissionTitle);
    }

    setIsInputEnabled(false);
  }, [title, currentSubmissionTitle]);

  const handleDescriptionConfirmationEdit = useCallback(() => {
    const value = description.slice(0, 250);
    if (description !== currentSubmissionDescription) {
      setDescription(value);
      dispatch(setCurrentSubmission({ ...currentSubmission, description: value }));
    } else {
      setDescription(currentSubmissionDescription);
    }

    if (value === '') {
      setIsDescriptionInputEnabled(true);
    } else {
      setIsDescriptionInputEnabled(false);
    }
  }, [description, currentSubmissionDescription]);

  const handleFocusOnInput = useCallback(() => {
    if (!isInputDisabled) {
      setIsInputEnabled(true);
      inputRef.current.focus();
    }
  }, [isInputDisabled]);

  const handleFocusOnDescriptionInput = useCallback(() => {
    if (!isInputDisabled) {
      setIsDescriptionInputEnabled(true);
      descriptionRef.current.focus();
    }
  }, [isInputDisabled]);

  const debouncedHandleOnClick = useCallback(debounce(handleFocusOnInput, 100), [
    handleFocusOnInput,
  ]);

  const debouncedHandleDescriptionOnClick = useCallback(
    debounce(handleFocusOnDescriptionInput, 100),
    [handleFocusOnDescriptionInput]
  );

  return (
    <>
      <Tooltip title={translate('rename_modal_template_name')} arrow>
        <span className="rename-template">
          <Button
            disabled={isInputDisabled}
            data-testid="controller-delete-button"
            color={!isInputDisabled ? 'primary' : 'default'}
            disableRipple
          >
            <RenameIcon className="edit-icon" />
          </Button>
        </span>
      </Tooltip>
      <div
        className="template__controllers_edit__container"
        data-testid="controller-edit-container"
      >
        <span
          className={cx('', {
            template__controllers_edit__title: isCurrentDescriptionDisplayed,
          })}
        >
          <EditTextField
            value={title}
            contentName="letter_name"
            icon={isSubmissionHasChanges ? <Indicator className="indicator" /> : <></>}
            inputRef={inputRef}
            isInputEnabled={isInputEnabled}
            handleConfirmation={handleConfirmationEdit}
            debouncedHandleOnClick={debouncedHandleOnClick}
            onChange={handleChangeEdit}
            isDisabled={isInputDisabled}
          />
        </span>
        {isCurrentDescriptionDisplayed && (
          <span className="template__controllers_edit__description">
            <EditTextField
              value={description}
              contentName="add_template_description"
              icon={null}
              inputRef={descriptionRef}
              isInputEnabled={isDescriptionInputEnabled}
              handleConfirmation={handleDescriptionConfirmationEdit}
              debouncedHandleOnClick={debouncedHandleDescriptionOnClick}
              onChange={handleDescriptionChangeEdit}
              isDisabled={isInputDisabled}
              charsCount={45}
            />
          </span>
        )}
      </div>
    </>
  );
};
